/**
 * ProvisionTimingBars — stacked-bar breakdown of a provision, normalized to the
 * total. Used as the fallback when a run has no per-step `startedAt` anchors
 * (older runs) and so can't be placed on the timeline; needs only durations.
 *
 * The backend stores a 3-level hierarchy flat: a `phase` row (`phase4_deploy`),
 * its `deploy_step` children, and `container` build rows that run inside
 * `app_up`. We reconstruct that shape as Deploy + indented steps (with builds
 * nested under "Start app"), plus a synthetic "Other setup" remainder row.
 *
 * @module features/sandboxes/components/overview/ProvisionTimingBars
 */
import React from 'react';
import { Tooltip, TooltipTrigger, cn } from '@chatsdet/ui';
import type { ProvisionTiming } from '../../hooks/useProvisionTimings';
import { describeTiming, OTHER_SETUP_META } from './provisionTimingMeta';

const fmtDuration = (ms: number): string =>
  ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;

interface Row {
  key: string;
  name: string;
  description: string;
  durationMs: number;
  /** Visual nesting: 0 = top-level, 1 = deploy step, 2 = container build. */
  indent: number;
  /** Container-build "of which" rows render in a quieter style. */
  muted?: boolean;
}

function buildRows(timings: ProvisionTiming[]): { rows: Row[]; baseMs: number } {
  const total = timings.find((t) => t.kind === 'phase' && t.step === 'total');
  const deploy = timings.find((t) => t.kind === 'phase' && t.step === 'phase4_deploy');
  const bySeq = (a: ProvisionTiming, b: ProvisionTiming) => a.sequence - b.sequence;
  const deploySteps = timings.filter((t) => t.kind === 'deploy_step').sort(bySeq);
  const containers = timings.filter((t) => t.kind === 'container').sort(bySeq);

  const nonTotal = timings.filter((t) => !(t.kind === 'phase' && t.step === 'total'));
  const baseMs = total?.durationMs ?? Math.max(1, ...nonTotal.map((t) => t.durationMs), 1);

  const rowFrom = (t: ProvisionTiming, indent: number, muted = false): Row => {
    const meta = describeTiming(t.kind, t.step);
    return { key: t.id, name: meta.name, description: meta.description, durationMs: t.durationMs, indent, muted };
  };

  const rows: Row[] = [];

  if (deploy) {
    rows.push(rowFrom(deploy, 0));
    const hasAppUp = deploySteps.some((s) => s.step === 'app_up');
    for (const step of deploySteps) {
      rows.push(rowFrom(step, 1));
      // Container builds run inside app_up — nest them as an "of which" breakdown.
      if (step.step === 'app_up') {
        for (const c of containers) rows.push(rowFrom(c, 2, true));
      }
    }
    // No app_up step but container rows exist — attach them under Deploy directly.
    if (!hasAppUp && containers.length > 0) {
      for (const c of containers) rows.push(rowFrom(c, 1, true));
    }
    if (total) {
      const otherMs = Math.max(0, total.durationMs - deploy.durationMs);
      if (otherMs > 0) {
        rows.push({
          key: 'other-setup',
          name: OTHER_SETUP_META.name,
          description: OTHER_SETUP_META.description,
          durationMs: otherMs,
          indent: 0,
        });
      }
    }
  } else {
    // Fallback (older / warm-pool runs without a deploy phase): flat list.
    // Health stages are a breakdown of `phase7_health`, not siblings of it, so
    // they indent as a muted "of which" the way container builds do under
    // app_up — otherwise the gate's time reads twice.
    for (const t of [...nonTotal].sort(bySeq)) {
      rows.push(t.kind === 'health_stage' ? rowFrom(t, 1, true) : rowFrom(t, 0));
    }
  }

  return { rows, baseMs };
}

export interface ProvisionTimingBarsProps {
  timings: ProvisionTiming[];
}

export function ProvisionTimingBars({ timings }: ProvisionTimingBarsProps) {
  const { rows, baseMs } = buildRows(timings);
  if (rows.length === 0) return null;

  return (
    <div className="divide-y divide-border">
      {rows.map((row) => (
        <div key={row.key} className="px-5 py-2 flex items-center gap-3">
          <div className="w-40 shrink-0" style={{ paddingLeft: row.indent * 14 }}>
            {row.description ? (
              <TooltipTrigger placement="top" delay={250}>
                <span
                  className={cn(
                    'inline-block max-w-full truncate align-bottom text-sm cursor-default',
                    'underline decoration-dotted decoration-muted-foreground/40 underline-offset-2',
                    row.muted ? 'text-muted-foreground' : 'font-medium'
                  )}
                >
                  {row.name}
                </span>
                <Tooltip className="max-w-[260px] leading-snug">{row.description}</Tooltip>
              </TooltipTrigger>
            ) : (
              <span
                className={cn(
                  'inline-block max-w-full truncate align-bottom text-sm',
                  row.muted ? 'text-muted-foreground' : 'font-medium'
                )}
              >
                {row.name}
              </span>
            )}
          </div>
          <div className="flex-1 h-2 rounded bg-muted overflow-hidden">
            <div
              className={cn('h-full', row.muted ? 'bg-primary/40' : 'bg-primary')}
              style={{ width: `${Math.max(2, (row.durationMs / baseMs) * 100)}%` }}
            />
          </div>
          <span className="text-xs font-mono text-muted-foreground w-14 text-right shrink-0">
            {fmtDuration(row.durationMs)}
          </span>
        </div>
      ))}
    </div>
  );
}

ProvisionTimingBars.displayName = 'ProvisionTimingBars';
